import React from 'react';
import {
    BodyLargeRegular,
    BodyMediumEmphasized,
    BodySmallRegular,
    FontFamilyName,
    MonoBodySmallRegular,
    ScrollView,
    XStack,
    YStack,
} from 'smoothie';

// Tamagui SizableText treats `fontFamily` as a font-token variant (e.g. $body), not a raw CSS family name
// use `style.fontFamily` for custom faces
const sourceSerifRegularStyle = {
    fontFamily: FontFamilyName.SourceSerifRegular,
};
const sourceSerifSemiBoldStyle = {
    fontFamily: FontFamilyName.SourceSerifSemiBold,
};
const sourceSerifItalicStyle = {
    fontFamily: FontFamilyName.SourceSerifItalic,
};

interface SimpleMarkdownProps {
    content: string;
    color?: string;
}

interface ParsedSegment {
    type: 'text' | 'bold' | 'italic' | 'code' | 'link';
    content: string;
    url?: string;
}

interface ParsedBlock {
    type:
        | 'paragraph'
        | 'header'
        | 'bullet'
        | 'numbered'
        | 'codeblock'
        | 'blockquote'
        | 'table'
        | 'hr';
    content: string;
    level?: number;
    language?: string;
    rows?: string[][];
}

/**
 * Markdown renderer for chat messages.
 * Supports: headers, bold, italic, code, code blocks, bullet/numbered lists,
 * blockquotes, tables, horizontal rules, and links.
 */
export const SimpleMarkdown = ({
    content,
    color = '$black',
}: SimpleMarkdownProps) => {
    const blocks = parseBlocks(content);

    return (
        <YStack gap={8}>
            {blocks.map((block, index) => (
                <MarkdownBlock key={index} block={block} color={color} />
            ))}
        </YStack>
    );
};

/**
 * Parse content into blocks (code blocks, tables, paragraphs, etc.)
 */
function parseBlocks(content: string): ParsedBlock[] {
    const blocks: ParsedBlock[] = [];
    const lines = content.split('\n');
    let i = 0;

    while (i < lines.length) {
        const line = lines[i] ?? '';

        // Code block (```)
        if (line.trim().startsWith('```')) {
            const language = line.trim().slice(3).trim();
            const codeLines: string[] = [];
            i++;
            while (
                i < lines.length &&
                !(lines[i] ?? '').trim().startsWith('```')
            ) {
                codeLines.push(lines[i] ?? '');
                i++;
            }
            blocks.push({
                type: 'codeblock',
                content: codeLines.join('\n'),
                language,
            });
            i++; // Skip closing ```
            continue;
        }

        // Table (starts with |)
        if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
            const tableRows: string[][] = [];
            while (
                i < lines.length &&
                (lines[i] ?? '').trim().startsWith('|')
            ) {
                const row = (lines[i] ?? '').trim();
                // Skip separator rows (|---|---|)
                if (!/^\|[\s\-:|]+\|$/.test(row)) {
                    const cells = row
                        .slice(1, -1) // Remove leading/trailing |
                        .split('|')
                        .map((cell) => cell.trim());
                    tableRows.push(cells);
                }
                i++;
            }
            if (tableRows.length > 0) {
                blocks.push({ type: 'table', content: '', rows: tableRows });
            }
            continue;
        }

        // Horizontal rule (---, ***, ___)
        if (/^(-{3,}|\*{3,}|_{3,})$/.test(line.trim())) {
            blocks.push({ type: 'hr', content: '' });
            i++;
            continue;
        }

        // Blockquote (>)
        if (line.trim().startsWith('>')) {
            const quoteLines: string[] = [];
            while (
                i < lines.length &&
                (lines[i] ?? '').trim().startsWith('>')
            ) {
                quoteLines.push((lines[i] ?? '').trim().replace(/^>\s?/, ''));
                i++;
            }
            blocks.push({ type: 'blockquote', content: quoteLines.join('\n') });
            continue;
        }

        // Header (# ## ###)
        const headerMatch = /^(#{1,4})\s+(.+)$/.exec(line);
        if (headerMatch != null) {
            const [, hashMarks, headerText] = headerMatch;
            blocks.push({
                type: 'header',
                content: headerText ?? '',
                level: hashMarks?.length ?? 1,
            });
            i++;
            continue;
        }

        // Bullet points (- or *)
        const bulletMatch = /^[-*]\s+(.+)$/.exec(line);
        if (bulletMatch != null) {
            blocks.push({
                type: 'bullet',
                content: bulletMatch[1] ?? '',
            });
            i++;
            continue;
        }

        // Numbered lists (1. 2. etc)
        const numberedMatch = /^(\d+)\.\s+(.+)$/.exec(line);
        if (numberedMatch != null) {
            blocks.push({
                type: 'numbered',
                content: numberedMatch[2] ?? '',
                level: parseInt(numberedMatch[1] ?? '0', 10),
            });
            i++;
            continue;
        }

        // Empty line
        if (line.trim() === '') {
            i++;
            continue;
        }

        // Regular paragraph
        blocks.push({ type: 'paragraph', content: line });
        i++;
    }

    return blocks;
}

const MarkdownBlock = ({
    block,
    color,
}: {
    block: ParsedBlock;
    color: string;
}) => {
    switch (block.type) {
        case 'header':
            return (
                <BodyMediumEmphasized
                    color={color}
                    fontSize={
                        block.level === 1
                            ? 20
                            : block.level === 2
                              ? 18
                              : block.level === 3
                                ? 16
                                : 14
                    }
                    paddingTop={4}
                    style={sourceSerifSemiBoldStyle}
                >
                    {block.content}
                </BodyMediumEmphasized>
            );

        case 'bullet':
            return (
                <XStack gap={8} paddingLeft={8}>
                    <BodyLargeRegular
                        color={color}
                        style={sourceSerifRegularStyle}
                    >
                        •
                    </BodyLargeRegular>
                    <YStack flex={1}>
                        <InlineMarkdown
                            text={block.content}
                            color={color}
                            useSerifFont
                        />
                    </YStack>
                </XStack>
            );

        case 'numbered':
            return (
                <XStack gap={8} paddingLeft={8}>
                    <BodyLargeRegular
                        color={color}
                        style={sourceSerifRegularStyle}
                    >
                        {block.level}.
                    </BodyLargeRegular>
                    <YStack flex={1}>
                        <InlineMarkdown
                            text={block.content}
                            color={color}
                            useSerifFont
                        />
                    </YStack>
                </XStack>
            );

        case 'codeblock':
            return (
                <YStack
                    backgroundColor="$gray300"
                    borderRadius={8}
                    padding={12}
                    overflow="hidden"
                >
                    <ScrollView
                        horizontal
                        showsHorizontalScrollIndicator={false}
                    >
                        <MonoBodySmallRegular color="$gray1100">
                            {block.content}
                        </MonoBodySmallRegular>
                    </ScrollView>
                </YStack>
            );

        case 'blockquote':
            return (
                <XStack
                    borderLeftWidth={3}
                    borderLeftColor="$gray600"
                    paddingLeft={12}
                    marginLeft={4}
                >
                    <BodyLargeRegular
                        color="$gray900"
                        fontStyle="italic"
                        style={sourceSerifItalicStyle}
                    >
                        {block.content}
                    </BodyLargeRegular>
                </XStack>
            );

        case 'table':
            return <MarkdownTable rows={block.rows ?? []} color={color} />;

        case 'hr':
            return (
                <YStack
                    height={1}
                    backgroundColor="$gray400"
                    marginVertical={8}
                />
            );

        case 'paragraph':
        default:
            return (
                <InlineMarkdown
                    text={block.content}
                    color={color}
                    useSerifFont={true}
                />
            );
    }
};

/**
 * Renders a markdown table with aligned columns
 */
const MarkdownTable = ({
    rows,
    color,
}: {
    rows: string[][];
    color: string;
}) => {
    if (rows.length === 0) return null;

    const headerRow = rows[0];
    if (headerRow == null) return null;
    const bodyRows = rows.slice(1);

    // Calculate column widths based on content (approximate: 8px per character + padding)
    const columnWidths = headerRow.map((_, colIndex) => {
        const maxLength = Math.max(
            ...rows.map((row) => (row[colIndex] ?? '').length),
        );
        // Min 60px, max 200px, scale by ~9px per character
        return Math.min(200, Math.max(60, maxLength * 9 + 16));
    });

    return (
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            <YStack
                borderWidth={1}
                borderColor="$gray400"
                borderRadius={8}
                overflow="hidden"
            >
                {/* Header row */}
                <XStack backgroundColor="$gray300">
                    {headerRow.map((cell, cellIndex) => (
                        <YStack
                            key={cellIndex}
                            padding={8}
                            width={columnWidths[cellIndex]}
                            borderRightWidth={
                                cellIndex < headerRow.length - 1 ? 1 : 0
                            }
                            borderRightColor="$gray400"
                        >
                            <BodyMediumEmphasized color={color}>
                                {cell}
                            </BodyMediumEmphasized>
                        </YStack>
                    ))}
                </XStack>

                {/* Body rows */}
                {bodyRows.map((row, rowIndex) => (
                    <XStack
                        key={rowIndex}
                        borderTopWidth={1}
                        borderTopColor="$gray400"
                    >
                        {headerRow.map((_, cellIndex) => (
                            <YStack
                                key={cellIndex}
                                padding={8}
                                width={columnWidths[cellIndex]}
                                borderRightWidth={
                                    cellIndex < headerRow.length - 1 ? 1 : 0
                                }
                                borderRightColor="$gray400"
                            >
                                <BodySmallRegular color={color}>
                                    {row[cellIndex] ?? ''}
                                </BodySmallRegular>
                            </YStack>
                        ))}
                    </XStack>
                ))}
            </YStack>
        </ScrollView>
    );
};

/**
 * Handles inline markdown: **bold**, *italic*, `code`, [links](url)
 */
const InlineMarkdown = ({
    text,
    color,
    useSerifFont,
}: {
    text: string;
    color: string;
    useSerifFont?: boolean;
}) => {
    const segments = parseInlineMarkdown(text);

    return (
        <BodyLargeRegular
            color={color}
            lineHeight="150%"
            style={useSerifFont ? sourceSerifRegularStyle : undefined}
        >
            {segments.map((segment, index) => {
                switch (segment.type) {
                    case 'bold':
                        return (
                            <BodyLargeRegular
                                key={index}
                                color={color}
                                style={
                                    useSerifFont
                                        ? sourceSerifSemiBoldStyle
                                        : undefined
                                }
                            >
                                {segment.content}
                            </BodyLargeRegular>
                        );
                    case 'italic':
                        return (
                            <BodyLargeRegular
                                key={index}
                                color={color}
                                style={
                                    useSerifFont
                                        ? sourceSerifItalicStyle
                                        : undefined
                                }
                            >
                                {segment.content}
                            </BodyLargeRegular>
                        );
                    case 'code':
                        return (
                            <MonoBodySmallRegular
                                key={index}
                                color="$gray1100"
                                backgroundColor="$gray300"
                                paddingHorizontal={4}
                                borderRadius={4}
                            >
                                {segment.content}
                            </MonoBodySmallRegular>
                        );
                    case 'link':
                        return (
                            <BodyLargeRegular
                                key={index}
                                color="$blue900"
                                textDecorationLine="underline"
                                style={
                                    useSerifFont
                                        ? sourceSerifRegularStyle
                                        : undefined
                                }
                            >
                                {segment.content}
                            </BodyLargeRegular>
                        );
                    default:
                        return (
                            <React.Fragment key={index}>
                                {segment.content}
                            </React.Fragment>
                        );
                }
            })}
        </BodyLargeRegular>
    );
};

/**
 * Parses inline markdown into segments
 */
function parseInlineMarkdown(text: string): ParsedSegment[] {
    const segments: ParsedSegment[] = [];
    let remaining = text;

    // Pattern to match **bold**, *italic*, `code`, or [link](url)
    const pattern = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`|\[([^\]]+)\]\(([^)]+)\))/;

    while (remaining.length > 0) {
        const match = pattern.exec(remaining);

        if (match?.index == null) {
            // No more patterns, add remaining text
            if (remaining.length > 0) {
                segments.push({ type: 'text', content: remaining });
            }
            break;
        }

        // Add text before the match
        if (match.index > 0) {
            segments.push({
                type: 'text',
                content: remaining.slice(0, match.index),
            });
        }

        // Determine the type of match
        if (match[2] != null) {
            // **bold**
            segments.push({ type: 'bold', content: match[2] });
        } else if (match[3] != null) {
            // *italic*
            segments.push({ type: 'italic', content: match[3] });
        } else if (match[4] != null) {
            // `code`
            segments.push({ type: 'code', content: match[4] });
        } else if (match[5] != null && match[6] != null) {
            // [link](url)
            segments.push({ type: 'link', content: match[5], url: match[6] });
        }

        remaining = remaining.slice(match.index + match[0].length);
    }

    return segments;
}
